import * as undoActions from 'owa-mail-undo';
import ApplyConversationActionContext from 'owa-mail-store/lib/store/schema/ApplyConversationActionContext';
import composeStore from 'owa-mail-compose-store/lib/store/composeStore';
import datapoints from './datapoints';
import deleteConversationService from '../services/deleteConversationService';
import deleteMailListRowsHelper from './helpers/deleteMailListRowsHelper';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getUniqueConversationContextsForRowKey from './helpers/getUniqueConversationContextsForRowKey';
import listViewStore, { getStore as getListViewStore } from 'owa-mail-list-store/lib/store/Store';
import restoreConversationService from '../services/restoreConversationService';
import showNotificationOnDelete from './helpers/showNotificationOnDelete';
import strings from 'owa-strings/owa-mail-strings';
import { select } from 'satcheljs/lib/legacy';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { actionNames, DeleteConversationStoreUpdate } from 'owa-mail-store-actions';
import { InstrumentationContext } from 'owa-mail-store';
import { doesTableSupportUndo } from 'owa-mail-undo';
import { getContextFolderIdForTable, TableView, getRowKeysFromRowIds } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { isGroupTableQuery } from 'owa-group-common';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { userMailInteractionAction } from 'owa-mail-actions';
import shouldPerformRowRemovalInstantUpdate from './helpers/shouldPerformRowRemovalInstantUpdate';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';

export interface DeleteConversationsFromTableState {
    originalTableView: TableView;
}

/**
 * The action worker which handles deleting the given conversations
 * @param conversationActionContexts - action contexts
 * @param conversationIds - the conversation that need to be deleted
 * @param originalTableView - the table view the conversation should be deleted from
 * @param disposalType - the disposal type
 * @param actionSource - the action source
 */
export let deleteConversationsWithoutConfirmDialog = createLegacyActionWithDatapoint(
    'deleteConversationsWithoutConfirmDialog',
    datapoints.deleteConversation
)(function deleteConversationsWithoutConfirmDialog(
    conversationActionContexts: ApplyConversationActionContext[],
    originalTableViewId: string,
    disposalType: DisposalType,
    instrumentationContexts: InstrumentationContext[],
    actionSource?: ActionSource
): Promise<void> {
    const conversationIds = conversationActionContexts.map(
        conversationContext => conversationContext.conversationId
    );
    const originalTableView = getListViewStore().tableViews.get(originalTableViewId);

    // raise stitch to delete conversation from list view and reading pane
    if (
        shouldPerformRowRemovalInstantUpdate(
            originalTableView.tableQuery,
            true /* isRowMovingToDeletedItemsFolder */
        )
    ) {
        raiseAction<DeleteConversationStoreUpdate>(actionNames.DeleteConversationStoreUpdate)(
            conversationIds,
            originalTableView
        );
    }

    undoActions.clearLastUndoableAction();

    const deletedItemsFolderId = folderNameToId('deleteditems');
    const allRowKeys = getRowKeysFromRowIds(conversationIds, originalTableView);
    const mailboxInfo = getMailboxInfo(originalTableView);
    const options = mailboxInfo.requestOptions;
    const contextFolderId = getContextFolderIdForTable(originalTableView);
    const allowUndo =
        doesTableSupportUndo(originalTableView.tableQuery.folderId, mailboxInfo) &&
        disposalType === 'MoveToDeletedItems';

    // Make service request
    const deleteConversationPromise = deleteConversationService(
        conversationActionContexts,
        contextFolderId,
        disposalType,
        options
    );

    if (allowUndo) {
        undoActions.addActionToUndoStack(async () => {
            // Wait for delete promise and make restore call after
            const response = await deleteConversationPromise;
            if (response.ResponseClass === 'Success') {
                return restoreConversationService(conversationActionContexts, deletedItemsFolderId);
            }
            return Promise.resolve();
        }, originalTableView.serverFolderId);
    }

    showNotificationOnDelete(conversationActionContexts.length, allowUndo);

    userMailInteractionAction('Delete', instrumentationContexts);

    returnTopExecutingActionDatapoint().markUserPerceivedTime();

    return processApplyConversationActionResponseMessage(
        deleteConversationPromise,
        allRowKeys,
        contextFolderId,
        originalTableView,
        true /* shouldRemoveRowsFromTombstone */
    );
});

/**
 * Initiates deleting of the given conversations
 * @param conversationRowKeys - the conversation that need to be deleted
 * @param originalTableViewId - the table view the conversation should be deleted from
 * @param isExplicitSoftDelete flag to indicate whether disposal type is soft delete
 * @param actionSource - the action source
 * @param state - some additional state required to complete the action
 */
export let deleteConversationsFromTable = select<DeleteConversationsFromTableState>({
    originalTableView: (conversationId: string, originalTableViewId: string) =>
        listViewStore.tableViews.get(originalTableViewId),
})(function deleteConversationsFromTable(
    conversationRowKeys: string[],
    originalTableViewId: string,
    isExplicitSoftDelete: boolean,
    instrumentationContexts: InstrumentationContext[],
    actionSource?: ActionSource,
    state?: DeleteConversationsFromTableState
) {
    const isGroupMailbox = isGroupTableQuery(state.originalTableView.tableQuery);

    const confirmDialogStrings = {
        deleteNumberOfRowsTitle: strings.deleteNumberOfConversations,
        deleteOneRowTitle: strings.deleteItem,
        confirmDeleteRows: isGroupMailbox
            ? strings.confirmDeletingGroupConversation
            : strings.confirmDeletingConversation,
    };

    return deleteMailListRowsHelper(
        conversationRowKeys,
        originalTableViewId,
        isExplicitSoftDelete,
        (rowKeys: string[], disposalType: DisposalType) => {
            const conversationActionContexts = getUniqueConversationContextsForRowKey(
                rowKeys,
                originalTableViewId
            );
            deleteConversationsWithoutConfirmDialog(
                conversationActionContexts,
                originalTableViewId,
                disposalType,
                instrumentationContexts,
                actionSource
            );
        },
        isReplyingToOneOfConversations,
        confirmDialogStrings,
        actionSource
    );
});

function isReplyingToOneOfConversations(conversationIds: string[]): boolean {
    const replyConversationId =
        composeStore.primaryComposeId &&
        composeStore.viewStates.get(composeStore.primaryComposeId).conversationId;
    return replyConversationId && conversationIds.indexOf(replyConversationId) >= 0;
}
